import { useCallback, useMemo, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { Button, Text, TextInput } from 'react-native-paper';

import { useFlagChat } from '@/features/api/hooks/communication-chat-events/useFlagChat';
import { useFlagComment } from '@/features/api/hooks/grapevine-comment-events/useFlagComment';
import { useFlagGroup } from '@/features/api/hooks/grapevine-group-events/useFlagGroup';
import { useFlagPost } from '@/features/api/hooks/grapevine-post-events/useFlagPost';
import { FlagCategoryEnum } from '@/features/flag/types/flagCategory';
import { FlagEntityTypeEnum } from '@/features/flag/types/flagEntityType';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { DropdownSelect } from '@/shared/components/DropdownSelect';
import { isEmptyOrNull } from '@/shared/utils/string';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    container: {
      flexDirection: 'column',
      alignItems: 'stretch',
      paddingHorizontal: 16,
      backgroundColor: theme.colors.surfaceContainerLow
    },
    subtitle: { textAlign: 'center', marginBottom: 32 },
    input: {
      minHeight: 180,
      maxHeight: 180,
      marginTop: 8,
      backgroundColor: theme.colors.surfaceContainerLow
    },
    button: { marginTop: 48, marginBottom: 32 }
  });

type Props = {
  entityType: FlagEntityTypeEnum;
  id: string;
  onSend: () => void;
};

export const FlagView: FC<Props> = ({ entityType, id, onSend }) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const { t } = useTranslation(LocalizationNameSpace.FLAGS);

  const flagChat = useFlagChat();
  const flagComment = useFlagComment();
  const flagPost = useFlagPost();
  const flagGroup = useFlagGroup();

  const isOptional = entityType !== 'CHAT';

  const [category, setCategory] = useState<FlagCategoryEnum>();
  const [text, setText] = useState<string>();

  const send = useCallback(() => {
    if (!category) return;
    switch (entityType) {
      case FlagEntityTypeEnum.Chat: {
        if (!text) return;
        flagChat.mutate({ chatId: id, comment: text, category: category });
        break;
      }
      case FlagEntityTypeEnum.Post: {
        flagPost.mutate({ postId: id, comment: text, category: category });
        break;
      }
      case FlagEntityTypeEnum.Comment: {
        flagComment.mutate({
          commentId: id,
          comment: text,
          category: category
        });
        break;
      }
      case FlagEntityTypeEnum.Group: {
        flagGroup.mutate({ groupId: id, comment: text, category: category });
        break;
      }
    }
    onSend();
  }, [
    category,
    entityType,
    flagChat,
    flagComment,
    flagGroup,
    flagPost,
    id,
    onSend,
    text
  ]);

  return (
    <View style={styles.container}>
      <Text variant="bodyLarge" style={styles.subtitle}>
        {t('subtitle')}
      </Text>

      <DropdownSelect<FlagCategoryEnum>
        label={t('kind')}
        placeholder={t('kind')}
        selectedOption={
          category
            ? {
                value: category,
                label: t(`categories.${category}`)
              }
            : undefined
        }
        options={Object.values(FlagCategoryEnum).map((value) => ({
          label: t(`categories.${value}`),
          value
        }))}
        onSelect={setCategory}
      />
      <TextInput
        style={styles.input}
        accessibilityLabel={t('accessibility.editorLabel')}
        accessibilityHint={t('accessibility.editorHint')}
        multiline
        textAlignVertical="top"
        mode="outlined"
        placeholder={t('descriptionHint') + (isOptional ? t('optional') : '')}
        label={t('description') + (isOptional ? t('optional') : '')}
        value={text}
        onChangeText={setText}
        maxLength={4000}
      />
      <Button
        style={styles.button}
        mode="contained"
        disabled={!category || (!isOptional && isEmptyOrNull(text))}
        onPress={send}
      >
        {t('send')}
      </Button>
    </View>
  );
};
